import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Send, Loader2, X, RotateCw } from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch } from '@/lib/apiFetch'
import type { AuthUser } from '@/lib/auth'

interface InviteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: AuthUser | null
}

interface OrgInfo {
  id: string
  name: string
  role: string
}

interface OrgMember {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  joined_at: string
}

interface Invitation {
  id: string
  email: string
  state: string
  expires_at: string
  created_at: string
  inviter_user_id: string | null
}

function memberDisplayName(m: OrgMember): string {
  const first = (m.first_name ?? '').trim()
  const last = (m.last_name ?? '').trim()
  if (first || last) return `${first} ${last}`.trim()
  return m.email.split('@')[0]
}

export function InviteModal({ open, onOpenChange, user }: InviteModalProps) {
  const [org, setOrg] = useState<OrgInfo | null>(null)
  const [members, setMembers] = useState<OrgMember[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const [emailDraft, setEmailDraft] = useState('')
  const [roleDraft, setRoleDraft] = useState<'admin' | 'member'>('member')
  const [isSending, setIsSending] = useState(false)
  const [busyInvitationId, setBusyInvitationId] = useState<string | null>(null)

  const isAdmin = org?.role === 'admin'

  const refreshAll = async () => {
    try {
      const [oRes, mRes, iRes] = await Promise.all([
        apiFetch('/api/org'),
        apiFetch('/api/org/members'),
        apiFetch('/api/org/invitations'),
      ])
      const o = oRes.ok ? ((await oRes.json()) as OrgInfo) : null
      const ms = mRes.ok ? ((await mRes.json()) as OrgMember[]) : []
      const inv = iRes.ok ? ((await iRes.json()) as Invitation[]) : []
      setOrg(o)
      setMembers(ms)
      setInvitations(inv)
    } catch {
      toast.error('Failed to load team data.')
    }
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    /* eslint-disable react-hooks/set-state-in-effect -- modal-open side effect: fetch team data when the dialog opens */
    setIsLoading(true)
    refreshAll().finally(() => {
      if (!cancelled) setIsLoading(false)
    })
    /* eslint-enable react-hooks/set-state-in-effect */
    return () => {
      cancelled = true
    }
  }, [open])

  const handleSend = async () => {
    const email = emailDraft.trim().toLowerCase()
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.')
      return
    }
    if (members.some((m) => m.email.toLowerCase() === email)) {
      toast.error('This email is already a member.')
      return
    }
    if (invitations.some((i) => i.email.toLowerCase() === email)) {
      toast.error('An invitation is already pending for this email.')
      return
    }
    setIsSending(true)
    try {
      const r = await apiFetch('/api/org/invitations', {
        method: 'POST',
        body: JSON.stringify({ email, role_slug: roleDraft }),
      })
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string }
        throw new Error(body.detail ?? `HTTP ${r.status}`)
      }
      const newInv = (await r.json()) as Invitation
      setInvitations((prev) => [newInv, ...prev])
      setEmailDraft('')
      setRoleDraft('member')
      toast.success(`Invitation sent to ${email}.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invitation.')
    } finally {
      setIsSending(false)
    }
  }

  const handleRevoke = async (invitationId: string) => {
    setBusyInvitationId(invitationId)
    try {
      const r = await apiFetch(`/api/org/invitations/${invitationId}`, { method: 'DELETE' })
      if (!r.ok && r.status !== 204) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string }
        throw new Error(body.detail ?? `HTTP ${r.status}`)
      }
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId))
      toast.success('Invitation revoked.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke invitation.')
    } finally {
      setBusyInvitationId(null)
    }
  }

  const handleResend = async (invitationId: string) => {
    setBusyInvitationId(invitationId)
    try {
      const r = await apiFetch(`/api/org/invitations/${invitationId}/resend`, { method: 'POST' })
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string }
        throw new Error(body.detail ?? `HTTP ${r.status}`)
      }
      const refreshed = (await r.json()) as Invitation
      setInvitations((prev) => prev.map((i) => (i.id === invitationId ? refreshed : i)))
      toast.success('Invitation re-sent.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resend invitation.')
    } finally {
      setBusyInvitationId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Invite your team</DialogTitle>
        </DialogHeader>

        {isLoading && !org ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  placeholder="Email address"
                  disabled={isSending}
                  className="flex-1 h-9 rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSend() }}
                />
                <Select value={roleDraft} onValueChange={(v) => setRoleDraft(v as 'admin' | 'member')} disabled={isSending}>
                  <SelectTrigger className="w-[110px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleSend}
                  disabled={isSending || !emailDraft.trim()}
                  className="bg-pharma-primary hover:bg-pharma-primary/90 text-white gap-1.5"
                  size="sm"
                >
                  {isSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Send invite
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Only admins can invite new members.
              </p>
            )}

            {invitations.length > 0 && (
              <div className="flex flex-col gap-1">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Pending invitations ({invitations.length})
                </h3>
                <div className="rounded-md border border-border divide-y divide-border">
                  {invitations.map((inv) => {
                    const busy = busyInvitationId === inv.id
                    return (
                      <div key={inv.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-foreground">{inv.email}</p>
                          <p className="text-xs text-muted-foreground">
                            Pending · expires {new Date(inv.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              disabled={busy}
                              onClick={() => handleResend(inv.id)}
                              title="Resend invitation"
                            >
                              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={busy}
                              onClick={() => handleRevoke(inv.id)}
                              title="Revoke invitation"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Active members ({members.length})
              </h3>
              <div className="rounded-md border border-border divide-y divide-border">
                {members.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">No members.</div>
                ) : (
                  members.map((m) => {
                    const isYou = m.user_id === user?.id
                    return (
                      <div key={m.user_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="flex flex-col min-w-0">
                          <span className="text-foreground truncate">
                            {memberDisplayName(m)}
                            {isYou && <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">{m.email}</span>
                        </div>
                        <span className="text-xs text-muted-foreground capitalize ml-2 shrink-0">
                          {m.role}
                        </span>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
